import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  List,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import {
  DayOfWeek,
  dayOfWeekLabels,
  dayOfWeekLabelsMobile,
  eventLabels,
  MyScheduleObject,
  MyScheduleSetting,
  MyThermostat,
  TimeOfDay,
  weekdayWeekendLabels,
} from '../../types';
import { deviceDefaultLimits } from '../../lib/limit-domains';
import { WdTab, WdTabs } from './weekday-tabs';
import { ScheduleItem } from './schedule-item';
import {
  Fahrenheit,
  TemperatureUnit,
} from '../../../../../../system/models/temperatureUnits';
import {
  EnumScheduleType,
  Maybe,
  Scalars,
} from '../../../../../../../types/generated-types';
import { useAuthenticator } from '../../../../../../auth/AuthenticationContext';
import { convertToPreferredThermostatSetpointLimitUnits } from '../../../../util/convert-to-preferred-thermostat-setpoint-limit-units';
import { daysToDisplay } from '../helpers/days-to-display';
import { useIsSmallScreen } from '../../../../../../system/SystemMediaQueriesProvider';

interface ScheduleProps {
  schedule?: MyScheduleObject;
  scheduleType?: EnumScheduleType;
  handleChange: (
    operation: 'edit_setpoints' | 'edit_time',
    dayOfWeek: DayOfWeek,
    timeOfDay: TimeOfDay,
    newTimeOfDay?: TimeOfDay,
    scheduleSetting?: MyScheduleSetting,
  ) => void;
  handleTypeChange: (type: EnumScheduleType) => void;
  temperatureUnit: TemperatureUnit;
  lastModified?: number;
  thermostat: MyThermostat;
}

export function Schedule(props: ScheduleProps) {
  const {
    schedule,
    scheduleType,
    handleChange,
    handleTypeChange,
    lastModified,
    thermostat,
  } = props;

  const selectedScheduleType = scheduleType ?? EnumScheduleType.SameEveryDay;
  const [thermostatSetpointLimits, setThermostatSetpointLimits] = useState<{
    maxCoolSetpointLimit: Maybe<Scalars['Float']> | undefined;
    minCoolSetpointLimit: Maybe<Scalars['Float']> | undefined;
    maxHeatSetpointLimit: Maybe<Scalars['Float']> | undefined;
    minHeatSetpointLimit: Maybe<Scalars['Float']> | undefined;
  }>();

  const { user } = useAuthenticator();

  const preferredUnits: TemperatureUnit = user?.preferredUnits ?? Fahrenheit;

  // If the schedule type is weekday-weekend, select Monday (the weekday) by default, otherwise select Sunday
  const [selectedDay, setSelectedDay] = useState(
    selectedScheduleType === EnumScheduleType.WeekdayWeekend
      ? DayOfWeek.MO
      : DayOfWeek.SU,
  );

  useEffect(() => {
    if (thermostat) {
      const defaults = deviceDefaultLimits(thermostat);
      // Preserve every applied reading; resolve only absent endpoints from the
      // shared device domains, including recognized legacy Core defaults.
      const formattedThermostatSetpointLimitValueUnits =
        convertToPreferredThermostatSetpointLimitUnits(
          {
            maxCoolSetpointLimit:
              thermostat?.maxCoolSetpointLimit?.value ??
              defaults.maxCoolSetpointLimit ??
              null,
            minCoolSetpointLimit:
              thermostat?.minCoolSetpointLimit?.value ??
              defaults.minCoolSetpointLimit ??
              null,
            maxHeatSetpointLimit:
              thermostat?.maxHeatSetpointLimit?.value ??
              defaults.maxHeatSetpointLimit ??
              null,
            minHeatSetpointLimit:
              thermostat?.minHeatSetpointLimit?.value ??
              defaults.minHeatSetpointLimit ??
              null,
          },
          preferredUnits,
        );

      setThermostatSetpointLimits(formattedThermostatSetpointLimitValueUnits);
    }
  }, [preferredUnits, thermostat]);

  useEffect(() => {
    if (!scheduleType) {
      // Set the initial schedule type if the thermostat does not currently have a schedule
      handleTypeChange(selectedScheduleType);
    }
  }, [handleTypeChange, scheduleType, selectedScheduleType]);

  const theme = useTheme();
  const isSmall = useIsSmallScreen();

  // Get label for the given day of the week, which differs depending on the schedule type
  function getTabLabel(dayOfWeek: DayOfWeek): string {
    const labels = isSmall ? dayOfWeekLabelsMobile : dayOfWeekLabels;
    if (selectedScheduleType === EnumScheduleType.Custom) {
      return labels[dayOfWeek];
    } else if (selectedScheduleType === EnumScheduleType.WeekdayWeekend) {
      return weekdayWeekendLabels[dayOfWeek];
    }
    return '';
  }

  const handleDaySelect = (
    _event: React.SyntheticEvent,
    newValue: DayOfWeek,
  ) => {
    setSelectedDay(newValue);
  };

  const handleTypeSelect = (type: EnumScheduleType) => {
    handleTypeChange(type);
    // If the new schedule type is weekday-weekend, select Monday (the weekday) by default, otherwise select Sunday
    if (type === EnumScheduleType.WeekdayWeekend) {
      setSelectedDay(DayOfWeek.MO);
    } else {
      setSelectedDay(DayOfWeek.SU);
    }
  };

  const handleEdit = (day: DayOfWeek) => {
    return (setting: MyScheduleSetting) => {
      return (oldTime: TimeOfDay, newTime?: TimeOfDay) => {
        if (newTime && parseInt(oldTime) - parseInt(newTime) !== 0) {
          handleChange('edit_time', day, oldTime, newTime, { ...setting });
        } else {
          handleChange('edit_setpoints', day, oldTime, undefined, {
            ...setting,
          });
        }
      };
    };
  };

  const dailySchedule = schedule?.value[selectedDay];
  const showTabs = daysToDisplay(selectedScheduleType).length > 1;

  return (
    <>
      <Box sx={{ p: 1 }}>
        <FormControl fullWidth>
          <InputLabel id="schedule-type-label">Schedule Type</InputLabel>
          <Select
            labelId="schedule-type-label"
            id="schedule-type"
            value={selectedScheduleType}
            label="Schedule Type"
            onChange={(e) =>
              handleTypeSelect(e.target.value as EnumScheduleType)
            }
          >
            <MenuItem value={EnumScheduleType.None}>None</MenuItem>
            <MenuItem value={EnumScheduleType.SameEveryDay}>
              Same Every Day
            </MenuItem>
            <MenuItem value={EnumScheduleType.WeekdayWeekend}>
              Weekday, Weekend
            </MenuItem>
            <MenuItem value={EnumScheduleType.Custom}>Custom</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {showTabs ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '10px',
          }}
        >
          <WdTabs
            variant="fullWidth"
            value={selectedDay}
            onChange={handleDaySelect}
            sx={{ width: '100%' }}
          >
            {schedule &&
              daysToDisplay(selectedScheduleType).map((dayOfWeek) => {
                return (
                  <WdTab
                    sx={{ minWidth: '45px' }}
                    key={dayOfWeek}
                    value={dayOfWeek}
                    label={getTabLabel(dayOfWeek)}
                  />
                );
              })}
          </WdTabs>
        </div>
      ) : null}
      <List
        sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}
        component="nav"
        aria-labelledby="nested-list-subheader"
        dense
      >
        {dailySchedule &&
          Object.entries(dailySchedule).map(
            ([timeOfDay, scheduleSetting], index) => {
              return (
                <Card
                  sx={{ marginBottom: '20px', backgroundColor: '#f5f5f5' }}
                  key={index}
                >
                  <CardHeader
                    title={
                      <Typography sx={{ fontSize: '1rem' }}>
                        {eventLabels[index]}
                      </Typography>
                    }
                  />
                  <ScheduleItem
                    id={`${index}`}
                    preferredUnits={preferredUnits}
                    key={selectedDay + '_' + timeOfDay}
                    time={timeOfDay}
                    setpointLimitSettings={{
                      maxHeatSetpointLimit:
                        thermostatSetpointLimits?.maxHeatSetpointLimit,
                      maxCoolSetpointLimit:
                        thermostatSetpointLimits?.maxCoolSetpointLimit,
                      minHeatSetpointLimit:
                        thermostatSetpointLimits?.minHeatSetpointLimit,
                      minCoolSetpointLimit:
                        thermostatSetpointLimits?.minCoolSetpointLimit,
                    }}
                    setting={scheduleSetting}
                    handleEdit={handleEdit(selectedDay)}
                  />
                </Card>
              );
            },
          )}
      </List>
      {lastModified && (
        <Box
          sx={{
            fontSize: 12,
            textAlign: 'center',
            color: theme.palette.grey[700],
            pb: 1,
          }}
        >
          Last modified {moment(lastModified).fromNow()}
        </Box>
      )}
    </>
  );
}
